import React from "react";
import { Skeleton } from "@mui/material";
import { cn } from "@/lib/utils";

interface PdsSkeletonProps {
  onDarkMode?: boolean;
}

const PdsSkeleton: React.FC<PdsSkeletonProps> = ({ onDarkMode = false }) => {
  const rows = [
    { labelWidth: "22%", localWidth: "20%", pdsWidth: "25%" },
    { labelWidth: "22%", localWidth: "15%", pdsWidth: "55%" },
    { labelWidth: "22%", localWidth: "35%", pdsWidth: "50%" },
    { labelWidth: "22%", localWidth: "25%", pdsWidth: "18%" },
    { labelWidth: "22%", localWidth: "18%", pdsWidth: "18%" },
    { labelWidth: "22%", localWidth: "18%", pdsWidth: "18%" },
    { labelWidth: "22%", localWidth: "18%", pdsWidth: "18%" },
    { labelWidth: "22%", localWidth: "18%", pdsWidth: "18%" },
    { labelWidth: "22%", localWidth: "18%", pdsWidth: "22%" },
  ];

  const skeletonSx = onDarkMode
    ? { backgroundColor: "rgba(255, 255, 255, 0.12)" }
    : undefined;

  return (
    <div className="p-4 space-y-3">
      {/* Patient Header */}
      <div
        className={cn(
          "flex items-center justify-between rounded-lg border p-3",
          onDarkMode ? "border-blue-900/40 bg-blue-950/20" : ""
        )}
        style={!onDarkMode ? { borderColor: "#b8d7f5", backgroundColor: "#f7fbff" } : {}}
      >
        <div className="flex items-center gap-3">
          <Skeleton
            variant="text"
            width={150}
            height={24}
            sx={{ borderRadius: "4px", ...skeletonSx }}
          />
          <Skeleton
            variant="rectangular"
            width={70}
            height={22}
            sx={{ borderRadius: "12px", ...skeletonSx }}
          />
        </div>

        <Skeleton
          variant="text"
          width={110}
          height={20}
          sx={{ borderRadius: "4px", ...skeletonSx }}
        />
      </div>

      {/* Hide identical fields */}
      <div className="flex items-center gap-2 px-1">
        <Skeleton
          variant="rectangular"
          width={26}
          height={16}
          sx={{ borderRadius: "10px", ...skeletonSx }}
        />
        <Skeleton
          variant="text"
          width={120}
          height={20}
          sx={{ borderRadius: "4px", ...skeletonSx }}
        />
      </div>

      {/* PDS Comparison Table */}
      <div
        className={cn(
          "overflow-hidden rounded-lg border",
          onDarkMode ? "border-slate-800" : "border-gray-200"
        )}
      >
        {/* Table Header */}
        <div
          className={cn(
            "grid grid-cols-[32%_34%_34%] min-h-[42px] border-b",
            onDarkMode
              ? "border-slate-800 bg-slate-900 text-slate-300"
              : "border-gray-200 bg-white"
          )}
        >
          <div className="flex items-center px-3">
            <Skeleton
              variant="text"
              width={45}
              height={20}
              sx={{ borderRadius: "4px", ...skeletonSx }}
            />
          </div>

          <div className="flex items-center gap-2 px-3">
            <Skeleton variant="circular" width={14} height={14} sx={skeletonSx} />
            <Skeleton
              variant="text"
              width={75}
              height={20}
              sx={{ borderRadius: "4px", ...skeletonSx }}
            />
          </div>

          <div
            className={cn(
              "flex items-center gap-2 px-3",
              onDarkMode ? "bg-blue-950/40" : "bg-[#f1f7fc]"
            )}
          >
            <Skeleton variant="circular" width={14} height={14} sx={skeletonSx} />
            <Skeleton
              variant="text"
              width={90}
              height={20}
              sx={{ borderRadius: "4px", ...skeletonSx }}
            />
          </div>
        </div>

        {/* Rows */}
        {rows.map((row, index) => (
          <div
            key={index}
            className={cn(
              "grid grid-cols-[32%_34%_34%] min-h-[43px] border-b last:border-b-0",
              onDarkMode ? "border-slate-800 bg-slate-900/40" : "border-gray-200"
            )}
          >
            {/* Field */}
            <div className="flex items-center px-3">
              <Skeleton
                variant="text"
                width={row.labelWidth}
                height={20}
                sx={{ borderRadius: "4px", ...skeletonSx }}
              />
            </div>

            {/* Local */}
            <div className="flex items-center gap-2 px-3">
              <Skeleton variant="circular" width={14} height={14} sx={skeletonSx} />
              <Skeleton
                variant="text"
                width={row.localWidth}
                height={20}
                sx={{ borderRadius: "4px", ...skeletonSx }}
              />
            </div>

            {/* PDS */}
            <div
              className={cn(
                "flex items-center gap-2 px-3",
                onDarkMode ? "bg-blue-950/30" : "bg-[#eaf3fb]"
              )}
            >
              <Skeleton variant="circular" width={14} height={14} sx={skeletonSx} />
              <Skeleton
                variant="text"
                width={row.pdsWidth}
                height={20}
                sx={{ borderRadius: "4px", ...skeletonSx }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <Skeleton
          variant="rectangular"
          width={65}
          height={36}
          sx={{ borderRadius: "6px", ...skeletonSx }}
        />

        <Skeleton
          variant="rectangular"
          width={125}
          height={36}
          sx={{ borderRadius: "6px", ...skeletonSx }}
        />
      </div>
    </div>
  );
};

export default PdsSkeleton;